import React from 'react';
import { observer } from 'mobx-react-lite';
import { OverlaySpinner, ProposalCard } from '@insurancemenu/imenu-components-lib';
import { toJS } from 'mobx';
import CartItemDelegate from 'store/pages/proposal-override-rates/delegates/cartItemDelegate';
import { useProposalOverrideRatesPageStore } from 'store/pages/proposal-override-rates/pageStore';
import ProductLineDelegate from 'store/pages/proposal-override-rates/delegates/productLineDelegate';
import ProductLineHeader from './ProductLineHeader';
import { CartItemsListStyled, ProductLineSectionStyled, ProposalsListStyled } from './styles';
import { OVERRIDE_RATES_PRODUCT_LINE_SECTION_TEST_ID, OVERRIDE_RATES_LOADING_SPINNER_TEST_ID } from './constants';

interface CartItemProps {
  productLineDelegate: ProductLineDelegate;
  cartItemDelegate: CartItemDelegate;
}

interface CartItemsCollectionProps {
  productLineDelegate: ProductLineDelegate;
  cartItemDelegates: CartItemDelegate[];
}

const CartItem: React.FC<CartItemProps> = observer(({ productLineDelegate, cartItemDelegate }) => {
  const { marketingTags, userRoles, isProposalConfirmed } = useProposalOverrideRatesPageStore();

  const convertedProductLineAttributes = productLineDelegate.productLineAttributes
    .map((dateObj) => toJS(dateObj))
    .slice();
  const convertedMarketingTags = marketingTags.map((dateObj) => toJS(dateObj)).slice();

  return (
    <ProposalCard
      type="OVERRIDE_RATES"
      userRoles={userRoles}
      isPlanConfigurable={false}
      portfolio={toJS(cartItemDelegate.wholePortfolio)}
      cartItem={toJS(cartItemDelegate.wholeCartItem)}
      productLineAttributes={convertedProductLineAttributes}
      marketingTags={convertedMarketingTags}
      isProposalConfirmed={isProposalConfirmed}
      onBrowsingChange={cartItemDelegate.setBrowsingCriteria}
      onOverrideRates={cartItemDelegate.redirectToOverrideRatesPage}
    />
  );
});

const CartItemsCollection: React.FC<CartItemsCollectionProps> = observer(
  ({ productLineDelegate, cartItemDelegates }) => {
    return (
      <CartItemsListStyled>
        {cartItemDelegates.map((cartItemDelegate) => (
          <CartItem
            key={cartItemDelegate.portfolioId}
            productLineDelegate={productLineDelegate}
            cartItemDelegate={cartItemDelegate}
          />
        ))}
      </CartItemsListStyled>
    );
  },
);

const OverrideRatesProposalPlans: React.FC = observer(() => {
  const { productLineDelegates, isLoading } = useProposalOverrideRatesPageStore();

  return (
    <ProposalsListStyled>
      {isLoading ? (
        <OverlaySpinner display data-test-id={OVERRIDE_RATES_LOADING_SPINNER_TEST_ID} />
      ) : (
        <>
          {productLineDelegates.map((productLineDelegate) => {
            return (
              <ProductLineSectionStyled
                key={`proposal_card_section_${productLineDelegate.productLineId}`}
                data-test-id={OVERRIDE_RATES_PRODUCT_LINE_SECTION_TEST_ID}
              >
                <ProductLineHeader productLineId={productLineDelegate.productLineId} />
                <CartItemsCollection
                  productLineDelegate={productLineDelegate}
                  cartItemDelegates={productLineDelegate.cartItemDelegates}
                />
              </ProductLineSectionStyled>
            );
          })}
        </>
      )}
    </ProposalsListStyled>
  );
});

export default OverrideRatesProposalPlans;
